import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, User, LayoutDashboard, Settings, HelpCircle, LogOut } from 'lucide-react';
import { Account } from '../../../types/account.types';

interface AccountDropdownProps {
  account?: Account;
  accountLoading?: boolean;
}

export const AccountDropdown: React.FC<AccountDropdownProps> = ({ account, accountLoading }) => {
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const profileButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target as Node) &&
        !profileButtonRef.current?.contains(event.target as Node)
      ) {
        setIsProfileMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsProfileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const handleSignOut = () => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/accounts/sign_out';
    const methodInput = document.createElement('input');
    methodInput.type = 'hidden';
    methodInput.name = '_method';
    methodInput.value = 'delete';
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    if (csrfToken) {
      const csrfInput = document.createElement('input');
      csrfInput.type = 'hidden';
      csrfInput.name = 'authenticity_token';
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);
    }
    form.appendChild(methodInput);
    document.body.appendChild(form);
    form.submit();
  };

  return (
    <div className="relative">
      <button
        ref={profileButtonRef}
        onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
        className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-gray-50"
      >
        {account?.profilePictureUrl && !avatarError ? (
          <div className="relative w-8 h-8 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center">
            <img
              src={account.profilePictureUrl}
              alt={account.fullName || account.email}
              className="max-w-full max-h-full object-contain"
              onError={() => setAvatarError(true)}
            />
          </div>
        ) : (
          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
            {accountLoading ? (
              <div className="animate-pulse w-full h-full bg-gray-300 rounded-full" />
            ) : (
              <User size={16} className="text-gray-600" />
            )}
          </div>
        )}
        <span className="hidden sm:block text-sm font-medium text-gray-900">
          {account?.firstName || account?.fullName?.split(' ')[0] || 'Account'}
        </span>
        <ChevronDown size={16} className="text-gray-500 hidden sm:block" />
      </button>
      
      {isProfileMenuOpen && (
        <div
          ref={profileMenuRef}
          className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg py-1 border z-[60]"
        >
          {account ? (
            <>
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="text-sm font-medium text-gray-900 truncate">
                  {account.fullName || 'Account'}
                </div>
                <div className="text-xs text-gray-500 mt-0.5 truncate">
                  {account.email}
                </div>
                {(account.profile?.company || account.title) && (
                  <div className="text-xs text-gray-500 mt-0.5 truncate">
                    {[account.title, account.profile?.company].filter(Boolean).join(' at ')}
                  </div>
                )}
              </div>
              <a
                href="/accounts"
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <LayoutDashboard className="inline h-4 w-4 mr-2" />
                Dashboard
              </a>
              <a
                href="/accounts/edit"
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <Settings className="inline h-4 w-4 mr-2" />
                Profile Settings
              </a>
              <a
                href="https://kb.connect.space/knowledge/information-for-event-attendees"
                target="_blank"
                rel="noopener noreferrer"
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <HelpCircle className="inline h-4 w-4 mr-2" />
                Help Center
              </a>
              <div className="border-t border-gray-100 my-1" />
              <button
                onClick={handleSignOut}
                className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-50"
              >
                <LogOut className="inline h-4 w-4 mr-2" />
                Sign out
              </button>
            </>
          ) : (
            <>
              <a
                href="/accounts/sign_in"
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Sign in
              </a>
              <a
                href="/accounts/sign_up"
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Sign up
              </a>
            </>
          )}
        </div>
      )}
    </div>
  );
};